import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { Badge } from '../Common/Badge';
import { Button } from '../Common/Button';
import { showToast } from '../../hooks/useToast';

interface Props {
  report: any;
  onChange: () => void;
}

export function ReportRow({ report, onChange }: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!window.confirm(`Delete "${report.name}"? This will also remove any dashboard widgets using it.`)) return;
    setBusy(true);
    try {
      await api.deleteSavedReport(report.id);
      showToast('success', 'Report deleted');
      onChange();
    } catch (err: any) {
      showToast('error', err.message ?? 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const open = () => {
    if (report.reportType === 'template' && report.templateKey) {
      navigate(`/templates/${report.templateKey}`);
    } else {
      // Custom report — re-run via API and display
      navigate(`/reports?id=${report.id}`);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg px-4 py-3 flex items-center justify-between hover:border-accent transition-colors">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <div className="text-sm font-medium text-text-primary truncate">{report.name}</div>
          <Badge variant={report.reportType === 'template' ? 'accent' : 'info'}>
            {report.reportType}
          </Badge>
          {report.isPublic && <Badge variant="neutral">public</Badge>}
        </div>
        {report.description && (
          <div className="text-xs text-text-muted truncate">{report.description}</div>
        )}
        <div className="text-[10px] uppercase tracking-wider text-text-hint mt-1">
          {report.ownerName} · updated {new Date(report.updatedAt).toLocaleDateString()}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-3">
        <Button size="sm" onClick={open}>Open</Button>
        {report.isOwner === 1 && (
          <Button size="sm" variant="danger" onClick={remove} disabled={busy}>Delete</Button>
        )}
      </div>
    </div>
  );
}
